import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, Shield, ChevronDown, LayoutDashboard, User, Wallet, Image, ScrollText, Gift, Settings } from "lucide-react";
import { ASSETS } from "@/lib/assets";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/", label: "Home", end: true },
  { to: "/chains", label: "Chains" },
  { to: "/tokenomics", label: "Tokenomics" },
  { to: "/roadmap", label: "Roadmap" },
  { to: "/wall", label: "Robbed Wall" },
  { to: "/battles", label: "Battles" },
  { to: "/leaderboard", label: "Leaderboard" },
  { to: "/memes", label: "Meme Gen" },
  { to: "/vault", label: "The Vault" },
];

const MENU = [
  { to: "/dashboard", label: "Dashboard", Icon: LayoutDashboard },
  { to: "/profile", label: "Profile", Icon: User },
  { to: "/settings#wallets", label: "Connected Wallets", Icon: Wallet },
  { to: "/my/memes", label: "My Memes", Icon: Image },
  { to: "/my/stories", label: "My Stories", Icon: ScrollText },
  { to: "/rewards", label: "Rewards", Icon: Gift },
  { to: "/settings", label: "Settings", Icon: Settings },
];

function UserMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const initial = (user.displayName || user.name || user.email || "R").charAt(0).toUpperCase();
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((v) => !v)} data-testid="user-menu-trigger"
        className="flex items-center gap-2 border-3 border-black bg-ink2 hover:border-neon px-2 py-1.5 transition-colors">
        {user.picture
          ? <img src={user.picture} alt="me" className="w-7 h-7 rounded-full border-2 border-neon" />
          : <span className="w-7 h-7 rounded-full bg-neon text-black font-display font-900 flex items-center justify-center text-sm">{initial}</span>}
        <span className="font-heading font-bold text-cream text-xs max-w-[110px] truncate">{user.displayName || user.name || "ROBBED USER"}</span>
        <ChevronDown className="w-4 h-4 text-dim" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-ink2 border-3 border-black shadow-hard z-50" data-testid="user-menu">
          <div className="px-4 py-3 border-b border-charcoal">
            <p className="font-mono text-[10px] text-dim uppercase">Signed in</p>
            <p className="font-mono text-xs text-cream truncate">{user.email || (user.wallets?.[0]?.shortAddress) || "Wallet user"}</p>
          </div>
          {user.role === "admin" && (
            <Link to="/admin" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2.5 font-heading font-bold text-xs uppercase text-gold hover:bg-ink3" data-testid="menu-admin">
              <Shield className="w-4 h-4" /> Admin
            </Link>
          )}
          {MENU.map((m) => (
            <Link key={m.label} to={m.to} onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-4 py-2.5 font-heading font-bold text-xs uppercase text-cream/85 hover:bg-ink3 hover:text-neon"
              data-testid={`menu-${m.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <m.Icon className="w-4 h-4" /> {m.label}
            </Link>
          ))}
          <button onClick={() => { setOpen(false); onLogout(); }}
            className="w-full flex items-center gap-2 px-4 py-2.5 font-heading font-bold text-xs uppercase text-danger hover:bg-ink3 border-t border-charcoal" data-testid="menu-logout">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const onLogout = async () => { await logout(); navigate("/"); };

  return (
    <header className="sticky top-0 z-50 bg-ink/90 backdrop-blur-md border-b-3 border-black">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <img src={ASSETS.circleLogo} alt="$ROBBED" className="w-10 h-10 rounded-full border-2 border-neon group-hover:animate-wobble" />
          <span className="font-display font-900 text-cream text-lg tracking-tight">$ROBBED</span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `px-3 py-2 font-heading font-bold uppercase text-xs tracking-wide transition-colors ${isActive ? "text-neon" : "text-cream/80 hover:text-neon"}`}>
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <UserMenu user={user} onLogout={onLogout} />
          ) : (
            <>
              <Link to="/login" className="btn-ghost !py-2 !px-3 !text-xs" data-testid="nav-login">Login</Link>
              <Link to="/register" className="btn-neon !py-2 !px-4 !text-xs" data-testid="nav-join">Join The Robbed</Link>
            </>
          )}
        </div>

        <button className="lg:hidden text-cream" onClick={() => setOpen((v) => !v)} data-testid="nav-menu-toggle">
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-ink2 border-t-3 border-black px-4 py-4 flex flex-col gap-1" data-testid="mobile-menu">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)} data-testid={`mnav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `px-3 py-3 font-heading font-bold uppercase text-sm border-b border-charcoal ${isActive ? "text-neon" : "text-cream/80"}`}>
              {l.label}
            </NavLink>
          ))}
          {user ? (
            <>
              {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="px-3 py-3 font-heading font-bold uppercase text-sm text-gold border-b border-charcoal">Admin</Link>}
              {MENU.map((m) => (
                <Link key={m.label} to={m.to} onClick={() => setOpen(false)} className="px-3 py-3 font-heading font-bold uppercase text-sm text-cream/80 border-b border-charcoal">{m.label}</Link>
              ))}
              <button onClick={() => { setOpen(false); onLogout(); }} className="btn-neon mt-3 !py-2 !text-xs">Logout</button>
            </>
          ) : (
            <div className="flex gap-2 mt-3">
              <Link to="/login" onClick={() => setOpen(false)} className="btn-ghost flex-1 !py-2 !text-xs">Login</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="btn-neon flex-1 !py-2 !text-xs">Join</Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
